import { z } from 'zod';

/* ------------------------------------------------------------ Prüfungen */

/** IBAN ohne Leerzeichen, in Großbuchstaben */
export function ibanNormalisieren(roh: string): string {
  return roh.replace(/\s+/g, '').toUpperCase();
}

/** Prüfsumme nach ISO 13616 (Modulo 97) – erkennt Tippfehler, nicht ob das Konto existiert */
export function ibanGueltig(roh: string): boolean {
  const iban = ibanNormalisieren(roh);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith('DE') && iban.length !== 22) return false;
  const umgestellt = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const z of umgestellt) {
    const wert = z >= 'A' ? String(z.charCodeAt(0) - 55) : z;
    for (const ziffer of wert) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest === 1;
}

/** In Vierergruppen für die Anzeige */
export function ibanFormatieren(roh: string): string {
  return ibanNormalisieren(roh).replace(/(.{4})(?=.)/g, '$1 ');
}

export function ustIdNormalisieren(roh: string): string {
  return roh.replace(/[\s.-]+/g, '').toUpperCase();
}

/** Länderkennungen der EU-USt-IdNr. (Griechenland EL, Nordirland XI) sowie Schweiz und Vereinigtes Königreich */
const UST_LAENDER = new Set(['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'GR', 'ES', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'XI', 'CH', 'GB']);

/** Formale Prüfung: Länderkennung + Nummer mit Ziffern, für Deutschland genau 9, für Österreich U + 8 Ziffern */
export function ustIdGueltig(roh: string): boolean {
  const u = ustIdNormalisieren(roh);
  if (u.startsWith('DE')) return /^DE\d{9}$/.test(u);
  if (u.startsWith('AT')) return /^ATU\d{8}$/.test(u);
  return UST_LAENDER.has(u.slice(0, 2)) && /^[A-Z]{2}[A-Z0-9+*]{2,13}$/.test(u) && /\d{2}/.test(u.slice(2));
}

/** Webadresse mit oder ohne https:// – nur Domains, keine Leerzeichen */
export function webGueltig(roh: string): boolean {
  return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(roh.trim());
}

/* ------------------------------------------------------------ Stammdaten */

const text = (max: number) => z.string().trim().max(max, `Höchstens ${max} Zeichen`).default('');
const optional = (max: number, pruefen: (v: string) => boolean, meldung: string) =>
  text(max).refine((v) => v === '' || pruefen(v), meldung);

/** Einstellungen → Marktplatz & Betreiber. Längen wie in der PHP-Middleware (MANDANT_STAMMDATEN). */
export const StammdatenSchema = z.object({
  name: z.string().trim().min(2, 'Bitte Namen des Marktplatzes angeben').max(120, 'Höchstens 120 Zeichen'),
  sortiment: text(160),
  firma: text(160),
  geschaeftsfuehrung: text(160),
  strasse: text(160),
  ort: text(120),
  land: z.string().trim().max(60, 'Höchstens 60 Zeichen').default('Deutschland'),
  telefon: optional(60, (v) => /^[+\d][\d\s()/-]{4,}$/.test(v), 'Bitte gültige Telefonnummer angeben'),
  email: optional(190, (v) => z.string().email().safeParse(v).success, 'Bitte gültige E-Mail angeben'),
  web: optional(190, webGueltig, 'Bitte gültige Webadresse angeben'),
  ustId: optional(40, ustIdGueltig, 'Bitte gültige USt-IdNr. angeben, z. B. DE123456789').transform(ustIdNormalisieren),
  registergericht: text(160),
  iban: optional(40, ibanGueltig, 'Die IBAN ist ungültig. Bitte prüfen Sie die Eingabe.').transform(ibanNormalisieren),
  /** Höhe des Logos auf Belegen (PDF) in Millimetern */
  logoHoehe: z.coerce.number().int('Bitte ganze Millimeter angeben').min(6, 'Mindestens 6 mm').max(40, 'Höchstens 40 mm').default(16),
});
export type StammdatenEingabe = z.input<typeof StammdatenSchema>;
export type Stammdaten = z.infer<typeof StammdatenSchema>;

export const StammdatenAntwort = StammdatenSchema.extend({
  kuerzel: z.string(),
  geaendertAm: z.string().nullable(),
});
export type StammdatenAntwort = z.infer<typeof StammdatenAntwort>;
